import Papa from 'papaparse';
import ColumnKey from './ColumnKey';
import ParsedRow from './ParsedRow';
import ParseResult from './ParseResult';
import RowIssue from './RowIssue';

export const REQUIRED_COLUMNS: ColumnKey[] = ['name', 'triggers', 'response'];
export const ALL_COLUMNS: ColumnKey[] = [
  'name',
  'triggers',
  'response',
  'topic'
];

const NAME_MAX_LENGTH = 255;
const TRIGGER_DELIMITER = '|';
const BOM_REGEX = /^\uFEFF/;

function normaliseHeader(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(BOM_REGEX, '').trim().toLowerCase();
}

function cellToString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  return '';
}

function splitTriggers(cell: string): string[] {
  return cell
    .split(TRIGGER_DELIMITER)
    .map((u) => u.trim())
    .filter((u) => u.length > 0);
}

function splitResponseBlocks(cell: string): string[] {
  return cell
    .split(/\r?\n/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0);
}

function buildMissingColumnsMessage(missing: ColumnKey[]): string {
  const plural = missing.length > 1 ? 's' : '';
  const missingList = missing.join(', ');
  const expected = ALL_COLUMNS.join(', ');
  return (
    `Missing required column${plural} in header row: ${missingList}. ` +
    `Expected headers: ${expected} (topic is optional).`
  );
}

function isBlankRow(row: ParsedRow): boolean {
  return (
    !row.name &&
    row.triggers.length === 0 &&
    row.responseBlocks.length === 0 &&
    !row.topic
  );
}

export async function parseFaqCsv(file: File): Promise<ParseResult> {
  const text = await file.text();
  const result = Papa.parse<string[]>(text, {
    header: false,
    skipEmptyLines: 'greedy'
  });

  if (result.errors.length > 0) {
    const first = result.errors[0];
    return {
      kind: 'header-error',
      message: `Could not parse CSV: ${first.message} (row ${first.row ?? '?'}).`
    };
  }

  const grid = result.data;
  if (grid.length === 0) {
    return { kind: 'header-error', message: 'The uploaded file is empty.' };
  }

  const headerRow = grid[0].map(normaliseHeader);
  const columnIndex: Record<ColumnKey, number> = {
    name: headerRow.indexOf('name'),
    triggers: headerRow.indexOf('triggers'),
    response: headerRow.indexOf('response'),
    topic: headerRow.indexOf('topic')
  };

  const missing = REQUIRED_COLUMNS.filter((c) => columnIndex[c] === -1);
  if (missing.length > 0) {
    return {
      kind: 'header-error',
      message: buildMissingColumnsMessage(missing)
    };
  }

  const rows: ParsedRow[] = grid
    .slice(1)
    .map((raw, idx): ParsedRow => {
      const name = cellToString(raw[columnIndex.name]).trim();
      const triggersCell = cellToString(raw[columnIndex.triggers]);
      const responseCell = cellToString(raw[columnIndex.response]);
      const topic =
        columnIndex.topic === -1
          ? ''
          : cellToString(raw[columnIndex.topic]).trim();
      return {
        lineNumber: idx + 2,
        name,
        triggers: splitTriggers(triggersCell),
        responseBlocks: splitResponseBlocks(responseCell),
        topic,
        issues: []
      };
    })
    .filter((row) => !isBlankRow(row));

  return { kind: 'parsed', rows };
}

function countNames(rows: ParsedRow[]): Map<string, number> {
  return rows.reduce<Map<string, number>>((acc, row) => {
    if (row.name) {
      acc.set(row.name, (acc.get(row.name) ?? 0) + 1);
    }
    return acc;
  }, new Map());
}

function validateRow(
  row: ParsedRow,
  nameCounts: Map<string, number>
): RowIssue[] {
  const issues: RowIssue[] = [];
  if (!row.name) {
    issues.push({ code: 'missing-name', message: 'Name is required' });
  } else if (row.name.length > NAME_MAX_LENGTH) {
    issues.push({
      code: 'name-too-long',
      message: `Name must be ${NAME_MAX_LENGTH} characters or fewer`
    });
  }
  if (row.triggers.length === 0) {
    issues.push({
      code: 'missing-triggers',
      message: `At least one trigger is required (separate multiple with '${TRIGGER_DELIMITER}')`
    });
  }
  if (row.responseBlocks.length === 0) {
    issues.push({ code: 'missing-response', message: 'Response is required' });
  }
  if (row.name && (nameCounts.get(row.name) ?? 0) > 1) {
    issues.push({
      code: 'duplicate-in-file',
      message: 'Duplicate name in upload'
    });
  }
  return issues;
}

export function validateRows(rows: ParsedRow[]): ParsedRow[] {
  const nameCounts = countNames(rows);
  return rows.map((row) => ({ ...row, issues: validateRow(row, nameCounts) }));
}

export function hasErrors(rows: ParsedRow[]): boolean {
  return rows.some((r) => r.issues.length > 0);
}
